import {
  accommodationDesignationMessages,
  customPriceBreakdownTitle,
  knownPriceBreakdownTitle,
} from 'checkout/constants/priceBreakdownTitles'
import { OFFER_TYPE_HOTEL, OFFER_TYPE_BED_BANK, OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_TOUR, OFFER_TYPE_TOUR_V2, OFFER_TYPE_CRUISE, OFFER_TYPE_VILLA } from 'constants/offer'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import { isolatePhoneNumber } from 'lib/format/formatPhoneNumber'
import { capitalise } from 'lib/string/stringUtils'

export function excludeNullOrUndefined<T>(item: T | undefined | null | false): item is T {
  return Boolean(item)
}

function getAccommodationDesignationLabels() {
  const intl = getCurrentIntl()

  return {
    accommodation: intl.formatMessage(accommodationDesignationMessages.accommodation),
    preAccommodation: intl.formatMessage(accommodationDesignationMessages.preAccommodation),
    postAccommodation: intl.formatMessage(accommodationDesignationMessages.postAccommodation),
  }
}

export function getDefaultAccommodationDesignation(): string {
  return getAccommodationDesignationLabels().accommodation
}

export function getAccommodationDesignationWithTourBundle(
  startDate: string,
  itemBundleId?: string,
  tourV2Items?: Array<App.Checkout.TourV2Item>,
): string {
  const {
    accommodation,
    postAccommodation,
    preAccommodation,
  } = getAccommodationDesignationLabels()

  const defaultDesignation = accommodation
  if (!itemBundleId || !tourV2Items?.length) {
    return defaultDesignation
  }
  const tourV2Item = tourV2Items.find(item => item.itemId === itemBundleId)
  if (!tourV2Item) {
    return defaultDesignation
  }
  return new Date(tourV2Item.startDate) > new Date(startDate) ? preAccommodation : postAccommodation
}

export function getAccommodationDesignationWithCruise(
  checkIn?: string,
  checkOut?: string,
  cruiseItems?: Array<App.Checkout.CruiseItem>,
): string {
  const {
    accommodation,
    postAccommodation,
    preAccommodation,
  } = getAccommodationDesignationLabels()

  const defaultDesignation = accommodation
  if (!checkIn || !checkOut || !cruiseItems?.length) {
    return defaultDesignation
  }

  // Check if hotel checkout matches cruise start (pre-cruise)
  const isPreCruise = cruiseItems.some(item => item.startDate === checkOut)
  if (isPreCruise) {
    return preAccommodation
  }

  // Check if hotel checkin matches cruise end (post-cruise)
  const isPostCruise = cruiseItems.some(item => item.endDate === checkIn)
  if (isPostCruise) {
    return postAccommodation
  }

  return defaultDesignation
}

/**
 * Maps a (possibly localised) accommodation designation label back to a known
 * breakdown title by comparing against the same label source that produced it.
 */
export function getBreakdownTitleFromDesignation(designation: string): App.Checkout.PriceBreakdownTitle {
  const labels = getAccommodationDesignationLabels()
  const keyByLabel: Partial<Record<string, App.Checkout.PriceBreakdownTitleKey>> = {
    [labels.preAccommodation]: 'preAccommodation',
    [labels.postAccommodation]: 'postAccommodation',
    [labels.accommodation]: 'accommodation',
  }
  const key = keyByLabel[designation]
  return key ? knownPriceBreakdownTitle(key) : customPriceBreakdownTitle(capitalise(designation))
}

export function getBreakdownTitle(offer: App.Checkout.AccommodationOfferView): App.Checkout.PriceBreakdownTitle {
  switch (offer.offerType) {
    case OFFER_TYPE_HOTEL:
    case OFFER_TYPE_BED_BANK:
    case OFFER_TYPE_ALWAYS_ON:
    case OFFER_TYPE_VILLA:
      return knownPriceBreakdownTitle('accommodation')
    case OFFER_TYPE_TOUR:
      return knownPriceBreakdownTitle('cruise')
    case OFFER_TYPE_TOUR_V2:
      return knownPriceBreakdownTitle('tour')
    case OFFER_TYPE_CRUISE:
      return knownPriceBreakdownTitle('cruise')
    default:
      return knownPriceBreakdownTitle('package')
  }
}

// Added extracted this from AccountTakeOverModal.tsx as needed values elsewhere - maybe can be updated later to use lib-regions flow?
export function getTollFreeSalesContactNumber(region: string, currentContact: App.GeoContactsTypes): App.GeoContacts {
  switch (region) {
    case 'AU':
      return {
        local: {
          number: '1300568529',
          humanReadable: isolatePhoneNumber('1300 568 529'),
        },
        international: {
          number: '1300568529',
          humanReadable: isolatePhoneNumber('1300 568 529'),
        },
        default: 'local',
        defaultContact: {
          number: '1300568529',
          humanReadable: isolatePhoneNumber('1300 568 529'),
        },
        autoVerification: false,
      }
    case 'NZ':
      return {
        local: {
          number: '0800856473',
          humanReadable: isolatePhoneNumber('0800 856 473'),
        },
        international: {
          number: '0800856473',
          humanReadable: isolatePhoneNumber('0800 856 473'),
        },
        default: 'local',
        defaultContact: {
          number: '0800856473',
          humanReadable: isolatePhoneNumber('0800 856 473'),
        },
        autoVerification: false,
      }
    case 'US':
      return {
        local: {
          number: '8888198182',
          humanReadable: isolatePhoneNumber('888 819 8182'),
        },
        international: {
          number: '8888198182',
          humanReadable: isolatePhoneNumber('888 819 8182'),
        },
        default: 'local',
        defaultContact: {
          number: '8888198182',
          humanReadable: isolatePhoneNumber('888 819 8182'),
        },
        autoVerification: false,
      }
    case 'GB':
      return {
        local: {
          number: '08006800855',
          humanReadable: isolatePhoneNumber('0800 680 0855'),
        },
        international: {
          number: '08006800855',
          humanReadable: isolatePhoneNumber('0800 680 0855'),
        },
        default: 'local',
        defaultContact: {
          number: '08006800855',
          humanReadable: isolatePhoneNumber('0800 680 0855'),
        },
        autoVerification: false,
      }
    default:
      return currentContact.highValue || currentContact.general
  }
}

export function isFlightWithEmbeddedInsurance(flight: App.Checkout.FlightItemFlight | App.Checkout.FlightItemViewJourney): boolean {
  return Boolean(flight.fareFamily?.withEmbeddedInsurance || flight.withEmbeddedInsurance)
}
